import { ComponentsStyles, createTheme, ThemeProvider as FThemeProvider } from '@fluentui/react';
import { createContext, HTMLAttributes, ReactNode } from 'react';

import {
  dialogContentStylesDefault,
  dialogFooterStylesDefault,
  dialogStylesDefault,
} from '../components/BaseDialog/BaseDialog.styles';
import { breadcrumbStylesDefault } from '../components/Breadcrumb/Breadcrumb.styles';
import { buttonStylesDefault } from '../components/Button/Button.styles';
import { checkboxStylesDefault } from '../components/Checkbox/Checkbox.styles';
import { choiceGroupOptionStylesDefault, choiceGroupStylesDefault } from '../components/ChoiceGroup/ChoiceGroup.styles';
import { comboBoxOptionStylesDefault, comboBoxStylesDefault } from '../components/ComboBox/ComboBox.styles';
import {
  detailsColumnStylesDefault,
  detailsHeaderStylesDefault,
  detailsListStylesDefault,
  detailsRowStylesDefault,
  groupHeaderStylesDefault,
} from '../components/DetailsList/DetailsList.styles';
import { dropdownStylesDefault } from '../components/Dropdown/Dropdown.styles';
import { imageStylesDefault } from '../components/Image/Image.styles';
import { linkStylesDefault } from '../components/Link/Link.styles';
import { loaderStylesProgressIndicatorDefault, loaderStylesSpinnerDefault } from '../components/Loader/Loader.styles';
import { messageBarStylesDefault } from '../components/MessageBar/MessageBar.styles';
import { navStylesDefault } from '../components/Nav/Nav.styles';
import { panelStylesDefault } from '../components/Panel/Panel.styles';
import { personaStylesDefault } from '../components/Persona/Persona.styles';
import { pivotStylesDefault } from '../components/Pivot/Pivot.styles';
import { searchBoxStylesDefault } from '../components/SearchBox/SearchBox.styles';
import { sliderStylesDefault } from '../components/Slider/Slider.styles';
import { textFieldStylesDefault } from '../components/TextField/TextField.styles';
import { tooltipHostStylesDefault } from '../components/TooltipHost/TooltipHost.styles';
import { IH2OTheme } from '../models/components.models';
import { defaultPalette, defaultSemanticColors } from '../themes/colors';
import {
  calloutContentStylesDefault,
  contextualMenuStylesDefault,
  labelStylesDefault,
  modalStylesDefault,
  toggleStylesDefault,
} from '../themes/themes';

export interface H2OThemeProviderProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  className?: string;
  theme?: IH2OTheme;
}

export const ThemeContext = createContext<IH2OTheme>({ defaultFontStyle: {}, palette: {}, semanticColors: {} });

export const h2oDefaultTheme: IH2OTheme = {
  defaultFontStyle: { fontFamily: 'Inter' },
  palette: defaultPalette,
  semanticColors: defaultSemanticColors,
};

const getFluentTheme = (theme: IH2OTheme) => {
  // Override Default styles of Fluent Components with H2O Styles
  const components: ComponentsStyles = {
    Breadcrumb: { styles: breadcrumbStylesDefault(theme) },
    Button: { styles: buttonStylesDefault(theme) },
    CalloutContent: { styles: calloutContentStylesDefault(theme) },
    ComboBox: { styles: comboBoxStylesDefault(theme) },
    ComboBoxOption: { styles: comboBoxOptionStylesDefault },
    Checkbox: { styles: checkboxStylesDefault(theme) },
    ChoiceGroup: { styles: choiceGroupStylesDefault(theme) },
    ChoiceGroupOption: { styles: choiceGroupOptionStylesDefault(theme) },
    ContextualMenu: { styles: contextualMenuStylesDefault(theme) },
    DetailsColumn: { styles: detailsColumnStylesDefault(theme) },
    DetailsHeader: { styles: detailsHeaderStylesDefault(theme) },
    DetailsList: { styles: detailsListStylesDefault },
    DetailsRow: { styles: detailsRowStylesDefault(theme) },
    Dialog: { styles: dialogStylesDefault(theme) },
    DialogContent: { styles: dialogContentStylesDefault(theme) },
    DialogFooter: { styles: dialogFooterStylesDefault },
    Dropdown: { styles: dropdownStylesDefault(theme) },
    GroupHeader: { styles: groupHeaderStylesDefault(theme) },
    Image: { styles: imageStylesDefault },
    Label: { styles: labelStylesDefault },
    Link: { styles: linkStylesDefault(theme) },
    MessageBar: { styles: messageBarStylesDefault(theme) },
    Modal: { styles: modalStylesDefault },
    Nav: { styles: navStylesDefault(theme) },
    Panel: { styles: panelStylesDefault(theme) },
    Persona: { styles: personaStylesDefault(theme) },
    Pivot: { styles: pivotStylesDefault(theme) },
    ProgressIndicator: { styles: loaderStylesProgressIndicatorDefault(theme) },
    Slider: { styles: sliderStylesDefault(theme) },
    SearchBox: { styles: searchBoxStylesDefault(theme) },
    Spinner: { styles: loaderStylesSpinnerDefault(theme) },
    TextField: { styles: textFieldStylesDefault(theme) },
    Toggle: { styles: toggleStylesDefault(theme) },
    TooltipHost: { styles: tooltipHostStylesDefault },
  };
  return createTheme({
    defaultFontStyle: theme.defaultFontStyle,
    semanticColors: {
      bodyBackground: theme.semanticColors?.bodyBackground,
      bodyFrameBackground: theme.semanticColors?.bodyFrameBackground,
    },
    components,
  });
};

export const H2OThemeProvider = (props: H2OThemeProviderProps) => {
  const { children, theme = { defaultFontStyle: {}, palette: {}, semanticColors: {} }, ...elementProps } = props;
  const newTheme = {
    defaultFontStyle: { ...h2oDefaultTheme.defaultFontStyle, ...theme.defaultFontStyle },
    palette: { ...h2oDefaultTheme.palette, ...theme.palette },
    semanticColors: { ...h2oDefaultTheme.semanticColors, ...theme.semanticColors },
  };
  return (
    <FThemeProvider theme={getFluentTheme(newTheme)} {...elementProps}>
      <ThemeContext.Provider value={newTheme}>{children}</ThemeContext.Provider>
    </FThemeProvider>
  );
};

// The toast and notification contexts now live in `@shared/lib/ui/components-alpha` (single
// provider each). The legacy `useToast`/`useNotification` hooks adapt to them; no reducer-backed
// message state remains here.
